// The eight close-up cards (REFOCUS §11c), read off the bird the player is
// looking at.
//
// Every card answers one question about one spot on the bird, and the answer is
// the sim's own state: a condition the disease model raised, or a meter the
// physiology model moved. A card that reports a healthy spot is therefore a
// claim the sim made, not one the UI invented — which is the whole point,
// because the card set this replaces reported "Crop · half full" at a good tone
// no matter what, and said nothing at all about a bird with crop trouble.
//
// Severity drives the tone rather than a hardcoded warning, so a mild case and
// a severe one do not read the same: the disease model already grades every
// condition mild/moderate/severe, and meters.ts already weights those three
// grades, so this follows the order the rest of the sim uses.

import type { ChickenEntity, ConditionId, ConditionSeverity } from '../core/types';
import { DEHYDRATED_HYDRATION } from './disease';

export interface InspectFinding {
  labelKey: string;
  tone: 'good' | 'warn' | 'danger';
}

/** The three grades meters.ts weights, in the order it weights them. */
const TONE_BY_SEVERITY: Record<ConditionSeverity, 'warn' | 'danger'> = {
  mild: 'warn',
  moderate: 'danger',
  severe: 'danger',
};

/**
 * One close-up spot: what it looks like when nothing is wrong, what it looks
 * like when the finding is there, and where the finding comes from.
 */
interface Spot {
  okKey: string;
  badKey: string;
  /** The condition that makes this spot a finding. */
  condition: ConditionId;
  /**
   * A meter that is a finding in its own right, for a spot you can see before
   * the disease model has named anything: a pale comb shows dehydration
   * (PHY-06) while the bird is still on her way to a `dehydrated` condition.
   */
  reading?: (bird: ChickenEntity) => boolean;
}

/**
 * The eight, in the order a keeper works down a bird. Mites and bumblefoot are
 * separate spots because the disease model treats them as separate problems: the
 * mites are contagious and want a dust bath, the bumblefoot wants a clean wrap,
 * and one card covering both left a player holding only one of them guessing
 * which remedy to reach for.
 */
const SPOTS: Spot[] = [
  { okKey: 'bird.finding.feetOk', badKey: 'bird.finding.feetBad', condition: 'bumblefoot' },
  { okKey: 'bird.finding.legsOk', badKey: 'bird.finding.legsMites', condition: 'scaly_leg_mites' },
  { okKey: 'bird.finding.ventOk', badKey: 'bird.finding.ventBad', condition: 'pasting_up' },
  { okKey: 'bird.finding.cropOk', badKey: 'bird.finding.cropBad', condition: 'crop_trouble' },
  {
    okKey: 'bird.finding.combOk',
    badKey: 'bird.finding.combPale',
    condition: 'dehydrated',
    reading: (bird) => bird.physiology.hydration <= DEHYDRATED_HYDRATION,
  },
  { okKey: 'bird.finding.breathOk', badKey: 'bird.finding.breathBad', condition: 'respiratory' },
  {
    okKey: 'bird.finding.droppingsOk',
    badKey: 'bird.finding.droppingsBad',
    condition: 'coccidiosis',
  },
  { okKey: 'bird.finding.fluffOk', badKey: 'bird.finding.fluffWet', condition: 'wet_and_cold' },
];

/** The worst grade of one condition on the bird, or null when she does not have it. */
export function severityOf(bird: ChickenEntity, condition: ConditionId): ConditionSeverity | null {
  const order: ConditionSeverity[] = ['mild', 'moderate', 'severe'];
  const held = bird.conditions.filter((entry) => entry.id === condition);
  if (held.length === 0) return null;
  return held.slice(1).reduce<ConditionSeverity>(
    (worst, entry) =>
      order.indexOf(entry.severity) > order.indexOf(worst) ? entry.severity : worst,
    held[0].severity,
  );
}

/** The eight cards, top to bottom, each one reading this bird's own state. */
export function inspectFindings(bird: ChickenEntity): InspectFinding[] {
  return SPOTS.map((spot) => {
    const severity = severityOf(bird, spot.condition);
    if (severity === null && !(spot.reading?.(bird) ?? false)) {
      return { labelKey: spot.okKey, tone: 'good' as const };
    }
    // A condition reads the grade the disease model gave it. The comb is the one
    // spot that can trip on a meter with no condition to grade, and the sim
    // calls a bird at that line severe, so that is the tone it gets.
    return {
      labelKey: spot.badKey,
      tone: severity === null ? 'danger' : TONE_BY_SEVERITY[severity],
    };
  });
}
